import React from 'react';
import { Text, Section, Link } from '@react-email/components';
import Layout, { LayoutTranslations } from './components/Layout';

export interface WelcomeVisitorTranslations {
  title: string;
  greeting: string;
  body: string;
  summary: string;
  labelEmail: string;
  labelLevel: string;
  statusConfirmed: string;
  paymentInstructionsTitle: string;
  paymentInstructionsBody: string;
  eventInformation: string;
  eventDates: string;
  eventLocation: string;
  eventHours: string;
  ctaText: string;
  keepForConfirmation: string;
  signOff: string;
  teamSignature: string;
  levelFree: string;
  levelPremium: string;
  levelVip: string;
  layout: LayoutTranslations;
}

interface WelcomeVisitorProps {
  name: string;
  email: string;
  level: 'free' | 'premium' | 'vip';
  includePaymentInstructions?: boolean;
  translations: WelcomeVisitorTranslations;
}

const levelColors: Record<string, string> = {
  free: '#6b7280',
  premium: '#2563eb',
  vip: '#d97706',
};

export default function WelcomeVisitor({
  name,
  email,
  level,
  includePaymentInstructions = false,
  translations: tx,
}: WelcomeVisitorProps) {
  const levelLabels: Record<string, string> = { free: tx.levelFree, premium: tx.levelPremium, vip: tx.levelVip };
  const levelLabel = levelLabels[level] || level;

  return (
    <Layout previewText={`${tx.title} ${name}`} translations={tx.layout}>
      <Text style={heading}>{tx.title}</Text>
      <Text style={paragraph}>{tx.greeting} <strong>{name}</strong>,</Text>
      <Text style={paragraph}>
        {tx.body.replace('{{level}}', levelLabel)}
      </Text>

      <Section style={summaryBox}>
        <Text style={summaryTitle}>{tx.summary}</Text>
        <Text style={summaryLine}>{tx.labelEmail}: {email}</Text>
        <Text style={summaryLine}>
          {tx.labelLevel}: <strong style={{ color: levelColors[level] }}>{levelLabel}</strong>
        </Text>
        <Text style={{ ...summaryLine, color: '#16a34a' }}>{tx.statusConfirmed}</Text>
      </Section>

      {includePaymentInstructions && (level === 'premium' || level === 'vip') && (
        <Section style={paymentBox}>
          <Text style={paymentTitle}>{tx.paymentInstructionsTitle}</Text>
          <Text style={paymentText}>{tx.paymentInstructionsBody.replace('{{level}}', levelLabel)}</Text>
        </Section>
      )}

      <Section style={eventBox}>
        <Text style={eventTitle}>{tx.eventInformation}</Text>
        <Text style={eventLine}>{tx.eventDates}</Text>
        <Text style={eventLine}>{tx.eventLocation}</Text>
        <Text style={eventLine}>{tx.eventHours}</Text>
      </Section>

      <Section style={ctaContainer}>
        <Link href="https://siportevent.com/visitor/dashboard" style={ctaButton}>{tx.ctaText}</Link>
      </Section>

      <Text style={footnote}>{tx.keepForConfirmation}</Text>
      <Text style={paragraph}>{tx.signOff}<br />{tx.teamSignature}</Text>
    </Layout>
  );
}

const heading: React.CSSProperties = { fontSize: '28px', fontWeight: 700, color: '#111827', margin: '0 0 20px 0' };
const paragraph: React.CSSProperties = { fontSize: '16px', lineHeight: '1.6', color: '#374151', margin: '0 0 16px 0' };
const summaryBox: React.CSSProperties = { backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', margin: '20px 0' };
const summaryTitle: React.CSSProperties = { fontSize: '16px', fontWeight: 600, color: '#1e3a5f', margin: '0 0 15px 0' };
const summaryLine: React.CSSProperties = { fontSize: '15px', color: '#374151', margin: '0 0 8px 0' };
const paymentBox: React.CSSProperties = { backgroundColor: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px', padding: '20px', margin: '20px 0' };
const paymentTitle: React.CSSProperties = { fontSize: '15px', fontWeight: 600, color: '#92400e', margin: '0 0 10px 0' };
const paymentText: React.CSSProperties = { fontSize: '14px', color: '#78350f', margin: 0, lineHeight: '1.6' };
const eventBox: React.CSSProperties = { backgroundColor: '#eff6ff', borderRadius: '8px', padding: '20px', margin: '20px 0' };
const eventTitle: React.CSSProperties = { fontSize: '15px', fontWeight: 600, color: '#1e40af', margin: '0 0 10px 0' };
const eventLine: React.CSSProperties = { fontSize: '14px', color: '#1e3a5f', margin: '0 0 5px 0' };
const ctaContainer: React.CSSProperties = { textAlign: 'center' as const, margin: '30px 0' };
const ctaButton: React.CSSProperties = { display: 'inline-block', padding: '14px 40px', backgroundColor: '#2563eb', color: '#ffffff', textDecoration: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '16px' };
const footnote: React.CSSProperties = { fontSize: '14px', color: '#94a3b8', textAlign: 'center' as const, margin: '0 0 20px 0' };
